import { useRef, useState } from 'react';
import { API_BASE } from '../../lib/apiBase.js';
import { Button } from '../ds/index.js';
import { PersonAvatar } from './PersonAvatar.js';
import {
  useMembersStore, apiHeaders, readFileAsDataUrl, photoErrorMessage, PHOTO_MAX_BYTES,
} from './membersStore.js';

/**
 * Photo de profil d'un membre : aperçu (photo, sinon visage du personnage),
 * choisir un fichier ou retirer. Enregistrée tout de suite (pas besoin du
 * bouton Enregistrer de la fenêtre). `endpoint` : `/auth/me/photo` (soi) ou
 * `/admin/members/{id}/photo` (admin).
 */
export function PhotoField({ userId, name, endpoint, size = 72 }: {
  userId: string;
  name: string;
  endpoint: string;
  size?: number;
}) {
  const hasPhoto = useMembersStore((s) => !!s.members.get(userId)?.photoVersion);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (photoVersion: string | null) => useMembersStore.getState().patchMember(userId, { photoVersion });

  const upload = async (file: File) => {
    setError(null);
    if (file.size > PHOTO_MAX_BYTES) { setError(photoErrorMessage(undefined, 413)); return; }
    setBusy(true);
    try {
      const image = await readFileAsDataUrl(file);
      const res = await fetch(`${API_BASE}${endpoint}`, { method: 'PUT', headers: apiHeaders(true), body: JSON.stringify({ image }) });
      if (res.ok) apply((await res.json() as { photoVersion: string }).photoVersion);
      else setError(photoErrorMessage((await res.json().catch(() => ({})) as { error?: string }).error, res.status));
    } catch {
      setError('La photo n’a pas pu être envoyée (réseau).');
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, { method: 'DELETE', headers: apiHeaders() });
      if (res.ok) apply(null);
      else setError(`La photo n’a pas pu être retirée (HTTP ${res.status}).`);
    } catch {
      setError('La photo n’a pas pu être retirée (réseau).');
    }
    setBusy(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Boutons sous la photo quand la place manque (colonne de 184 px de l'éditeur d'avatar). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <PersonAvatar userId={userId} name={name} size={size} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', minWidth: 0 }}>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? 'Envoi…' : hasPhoto ? 'Changer la photo' : 'Ajouter une photo'}
          </Button>
          {hasPhoto && <Button variant="ghost" size="sm" disabled={busy} onClick={() => { void remove(); }}>Retirer la photo</Button>}
        </div>
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }}
      />
      {error && <p role="alert" className="pl-help" style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p>}
    </div>
  );
}
